/******/ (() => { // webpackBootstrap
class CollapsibleButton extends HTMLElement {
  constructor() {
    super();
    this.THROTTLE_INTERVAL = 100;
    this.bottomGapHeight = 15;
    this.throttleTimer = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.resetHeight = this.resetHeight.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleShopifyBlockSelect = this.handleShopifyBlockSelect.bind(this);
    this.handleShopifyBlockDeselect = this.handleShopifyBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.toggle);
    this.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);

    if (this.collapsibleContent) {
      this.collapsibleContent.removeEventListener('click', this.resetHeight);
    }

    document.removeEventListener('shopify:block:select', this.handleShopifyBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleShopifyBlockDeselect);
  }

  init() {
    window.wetheme.webcomponentRegistry.register({ key: 'collapsible-button' });

    this.tab = this.parentElement;
    this.content = this.nextElementSibling;
    this.collapsibleContent = this.tab?.querySelector('.collapsible-content');
    this.wrapper = this.tab?.parentElement;

    if (
      !this.tab ||
      !this.content ||
      !this.content.classList.contains('collapsible-content') ||
      !this.tab.classList.contains('collapsible-tab')
    ) {
      return;
    }

    // Set initial height based on state
    if (this.tab.classList.contains('open')) {
      this.setTransitionHeight(this.content, false);
      this.content.style.overflow = 'visible';
    } else {
      this.content.style.height = '0px';
      this.content.style.overflow = 'hidden';
    }

    // Handle click
    this.addEventListener('click', this.toggle);

    // Handle enter key
    this.addEventListener('keydown', this.handleKeydown);

    // Handle window resize
    window.addEventListener('resize', this.handleResize);

    // Handle collapsible tab content size change
    this.content.addEventListener('click', this.resetHeight);

    // Handle Shopify theme editor events
    document.addEventListener('shopify:block:select', this.handleShopifyBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleShopifyBlockDeselect);
  }

  getContentHeight() {
    const contentInner = this.content?.querySelector('.content');
    if (!contentInner) return '0px';
    return contentInner.offsetHeight + this.bottomGapHeight + 'px';
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    }
  }

  handleResize() {
    this.throttle(this.resetHeight, this.THROTTLE_INTERVAL);
  }

  resetHeight() {
    if (!this.tab.classList.contains('open')) return;
    this.setTransitionHeight(this.content, false);
  }

  toggle() {
    if (this.tab.classList.contains('open')) {
      this.hideDetails();
    } else {
      this.showDetails();
    }
  }

  showDetails() {
    this.closeSiblingTabs();
    this.setTransitionHeight(this.content, false);
    this.tab.classList.add('open');

    if (this.collapsibleContent) {
      this.collapsibleContent.addEventListener(
        'transitionend',
        this.transitionEndHandler.bind(this),
        { once: true }
      );
    }
  }

  hideDetails() {
    this.setTransitionHeight(this.content, true);
    this.tab.classList.remove('open');

    if (this.collapsibleContent) {
      this.collapsibleContent.style.overflow = 'hidden';
    }
  }

  closeSiblingTabs() {
    if (!this.wrapper) return;

    const siblingTabs = this.wrapper.querySelectorAll('.collapsible-tab.open');

    siblingTabs.forEach((siblingTab) => {
      if (siblingTab === this.tab) return;

      siblingTab.classList.remove('open');

      const siblingContent = siblingTab.querySelector('.collapsible-content');
      if (siblingContent) {
        siblingContent.style.height = '0px';
        siblingContent.style.overflow = 'hidden';
      }
    });
  }

  setTransitionHeight(container, isClosing) {
    if (!container) return;

    if (isClosing) {
      container.style.height = '0px';
    } else {
      container.style.height = this.getContentHeight();
    }
  }

  transitionEndHandler(event) {
    if (event.propertyName === 'height' && this.collapsibleContent) {
      this.collapsibleContent.style.overflow = 'visible';
    }
  }

  handleShopifyBlockSelect(event) {
    if (this.tab.dataset.openByDefault === 'true') return;

    this.blockId = this.dataset.blockId;

    if (this.blockId && event.detail.blockId === this.blockId) {
      this.showDetails();
    }
  }

  handleShopifyBlockDeselect(event) {
    if (this.tab.dataset.openByDefault === 'true') return;

    this.blockId = this.dataset.blockId;

    // Only close this specific block when it is deselected
    if (this.blockId && event.detail && event.detail.blockId === this.blockId) {
      this.hideDetails();
    }
  }

  throttle(callback, time) {
    if (this.throttleTimer) return;

    this.throttleTimer = true;

    setTimeout(() => {
      callback.call(this);
      this.throttleTimer = false;
    }, time);
  }
}

// Only define the custom element if it doesn't already exist
if (!customElements.get('collapsible-button')) {
  customElements.define('collapsible-button', CollapsibleButton);
}

/******/ })()
;